/**
 * Unified Header Component
 * Single header bar replacing both the app header and channel/DM header
 *
 * Left: Channel/DM context info
 * Right: Search, Bookmarks, Voice call (DM), DND, More menu
 */

import React, { useState, useRef, useEffect } from 'react';
import { useAppStore } from '@/store/app.store';
import { useChannel } from '@/hooks/useChannels';
import { useDMThread } from '@/hooks/useDMs';
import { useAuth } from '@/hooks/useAuth';
import { useVoice } from '@/contexts/VoiceContext';
import { useLiveKit } from '@/contexts/LiveKitContext';
import Avatar from '@/components/common/Avatar';
import { DNDIndicator } from './DNDIndicator';
import { NotificationBell } from './NotificationBell';
import { CURRENT_VERSION } from '@/components/changelog';
import { isElectron } from '@/services/platform';

interface UnifiedHeaderProps {
  onOpenSettings: () => void;
  onOpenAdminPanel: (section: 'users' | 'smtp' | 'storage' | 'analytics') => void;
  onOpenWhatsNew: () => void;
  onShowChangelog: () => void;
  fileDragActive?: boolean;
}

export const UnifiedHeader: React.FC<UnifiedHeaderProps> = ({
  onOpenAdminPanel,
  onOpenWhatsNew,
  onShowChangelog,
  fileDragActive = false,
}) => {
  const {
    activeChannelId,
    activeDMThreadId,
    rightPanelView,
    setRightPanelView,
    searchOpen,
    setSearchOpen,
  } = useAppStore();

  const { user } = useAuth();
  const { data: channel, isLoading: channelLoading } = useChannel(activeChannelId);
  const { data: dmThread, isLoading: dmThreadLoading } = useDMThread(activeDMThreadId);
  const { startCall, voiceState } = useVoice();
  const { state: liveKitState } = useLiveKit();

  const [menuOpen, setMenuOpen] = useState(false);
  const menuRef = useRef<HTMLDivElement>(null);

  // Close menu when clicking outside
  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (menuRef.current && !menuRef.current.contains(event.target as Node)) {
        setMenuOpen(false);
      }
    };
    if (menuOpen) {
      document.addEventListener('mousedown', handleClickOutside);
    }
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, [menuOpen]);

  return (
    <div className="relative h-14 bg-white dark:bg-dark-bg-secondary border-b border-gray-200/60 dark:border-dark-border-primary shadow-[0_1px_2px_rgba(0,0,0,0.04)] dark:shadow-[0_1px_2px_rgba(0,0,0,0.2)] flex items-center justify-between px-5 flex-shrink-0">
      {/* Electron: invisible drag handle behind all content. Hidden during file drags to avoid conflicts. */}
      {isElectron() && !fileDragActive && (
        <div className="absolute inset-0 electron-drag" />
      )}
      {/* Left: Context info */}
      <div className={`relative flex items-center gap-3 min-w-0${isElectron() ? ' electron-no-drag' : ''}`}>
        {/* Loading state */}
        {(channelLoading || dmThreadLoading) && (
          <div className="h-5 w-40 bg-gray-200 dark:bg-dark-bg-input rounded animate-pulse" />
        )}

        {/* Channel info */}
        {!channelLoading && !dmThreadLoading && channel && (
          <>
            <h2 className="text-base font-semibold text-gray-900 dark:text-dark-text-primary truncate">
              # {channel.name}
            </h2>
            {channel.description && (
              <span className="text-sm text-gray-400 dark:text-dark-text-muted truncate hidden sm:block">
                {channel.description}
              </span>
            )}
          </>
        )}

        {/* DM thread info */}
        {!channelLoading && !dmThreadLoading && dmThread?.members && dmThread.members.length > 0 && (
          <>
            {dmThread.is_group ? (
              <>
                <div className="flex -space-x-2">
                  {dmThread.members.slice(0, 3).map((member) => (
                    <Avatar key={member.id} user={member} size="xs" />
                  ))}
                </div>
                <h2 className="text-base font-semibold text-gray-900 dark:text-dark-text-primary truncate">
                  {dmThread.members.map((m) => m.display_name?.split(' ')[0]).join(', ')}
                </h2>
              </>
            ) : dmThread.other_user ? (
              <>
                <Avatar user={dmThread.other_user} size="sm" />
                <h2 className="text-base font-semibold text-gray-900 dark:text-dark-text-primary truncate">
                  {dmThread.other_user.display_name}
                </h2>
              </>
            ) : null}
          </>
        )}

        {/* Nothing selected */}
        {!channelLoading && !dmThreadLoading && !channel && !dmThread && (
          <h1 className="text-lg font-bold bg-gradient-to-r from-primary-600 to-primary-400 bg-clip-text text-transparent">
            HighFive
          </h1>
        )}
      </div>

      {/* Right: Action buttons */}
      <div className={`relative flex items-center gap-2${isElectron() ? ' electron-no-drag' : ''}`}>
        {/* Search Button */}
        <button
          onClick={() => setSearchOpen(!searchOpen)}
          className={`p-2 rounded-lg transition-colors relative group ${
            searchOpen
              ? 'bg-primary-100 dark:bg-primary-900/30 text-primary-600 dark:text-primary-400'
              : 'hover:bg-gray-100 dark:hover:bg-dark-bg-elevated text-gray-600 dark:text-dark-text-secondary'
          }`}
          title="Търсене (Ctrl+F)"
        >
          <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0z" />
          </svg>
        </button>

        {/* Bookmarks Button */}
        <button
          onClick={() => {
            if (rightPanelView === 'bookmarks') {
              setRightPanelView(null);
            } else {
              setRightPanelView('bookmarks');
            }
          }}
          className={`p-2 rounded-lg transition-colors ${
            rightPanelView === 'bookmarks'
              ? 'bg-yellow-100 dark:bg-yellow-900/30 text-yellow-600 dark:text-yellow-400'
              : 'hover:bg-gray-100 dark:hover:bg-dark-bg-elevated text-gray-600 dark:text-dark-text-secondary'
          }`}
          title="Запазени съобщения"
        >
          <svg className="w-5 h-5" fill={rightPanelView === 'bookmarks' ? 'currentColor' : 'none'} stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 5a2 2 0 012-2h10a2 2 0 012 2v16l-7-3.5L5 21V5z" />
          </svg>
        </button>

        {/* Voice Call Button - DMs only */}
        {dmThread && activeDMThreadId && !voiceState.activeSession && !liveKitState.dmThreadId && (
          <button
            onClick={() => startCall(activeDMThreadId)}
            className="p-2 text-gray-500 dark:text-dark-text-secondary hover:text-green-600 dark:hover:text-green-400 hover:bg-green-50 dark:hover:bg-green-900/20 rounded-lg transition-colors"
            title={dmThread.is_group ? 'Започни групово обаждане' : 'Започни обаждане'}
          >
            <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M3 5a2 2 0 012-2h3.28a1 1 0 01.948.684l1.498 4.493a1 1 0 01-.502 1.21l-2.257 1.13a11.042 11.042 0 005.516 5.516l1.13-2.257a1 1 0 011.21-.502l4.493 1.498a1 1 0 01.684.949V19a2 2 0 01-2 2h-1C9.716 21 3 14.284 3 6V5z" />
            </svg>
          </button>
        )}

        {/* Notification Bell */}
        <NotificationBell />

        {/* DND Indicator */}
        <DNDIndicator />

        {/* More Menu */}
        <div className="relative" ref={menuRef}>
          <button
            onClick={() => setMenuOpen(!menuOpen)}
            className="p-2 hover:bg-gray-100 dark:hover:bg-dark-bg-elevated rounded-lg transition-colors"
            title="Още опции"
          >
            <svg className="w-5 h-5 text-gray-600 dark:text-dark-text-secondary" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 5v.01M12 12v.01M12 19v.01M12 6a1 1 0 110-2 1 1 0 010 2zm0 7a1 1 0 110-2 1 1 0 010 2zm0 7a1 1 0 110-2 1 1 0 010 2z" />
            </svg>
          </button>

          {menuOpen && (
            <div className="absolute right-0 top-full mt-1 w-48 bg-white dark:bg-dark-bg-elevated border border-gray-100 dark:border-dark-border-secondary rounded-xl shadow-lg z-50 py-1 animate-modal-in">
              {(user?.role === 'admin' || user?.role === 'super_admin') && (
                <>
                  <button
                    onClick={() => { onOpenAdminPanel('users'); setMenuOpen(false); }}
                    className="w-full px-4 py-2 text-left text-sm text-gray-700 dark:text-dark-text-primary hover:bg-gray-100 dark:hover:bg-dark-bg-input flex items-center space-x-2"
                  >
                    <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 6V4m0 2a2 2 0 100 4m0-4a2 2 0 110 4m-6 8a2 2 0 100-4m0 4a2 2 0 110-4m0 4v2m0-6V4m6 6v10m6-2a2 2 0 100-4m0 4a2 2 0 110-4m0 4v2m0-6V4" />
                    </svg>
                    <span>Администрация</span>
                  </button>
                  <button
                    onClick={() => { onOpenAdminPanel('analytics'); setMenuOpen(false); }}
                    className="w-full px-4 py-2 text-left text-sm text-gray-700 dark:text-dark-text-primary hover:bg-gray-100 dark:hover:bg-dark-bg-input flex items-center space-x-2"
                  >
                    <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 19v-6a2 2 0 00-2-2H5a2 2 0 00-2 2v6a2 2 0 002 2h2a2 2 0 002-2zm0 0V9a2 2 0 012-2h2a2 2 0 012 2v10m-6 0a2 2 0 002 2h2a2 2 0 002-2m0 0V5a2 2 0 012-2h2a2 2 0 012 2v14a2 2 0 01-2 2h-2a2 2 0 01-2-2z" />
                    </svg>
                    <span>Аналитика</span>
                  </button>
                  <div className="border-t border-gray-200 dark:border-dark-border-primary my-1" />
                </>
              )}

              <button
                onClick={() => { onOpenWhatsNew(); setMenuOpen(false); }}
                className="w-full px-4 py-2 text-left text-sm text-gray-700 dark:text-dark-text-primary hover:bg-gray-100 dark:hover:bg-dark-bg-input flex items-center space-x-2"
              >
                <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 3v4M3 5h4M6 17v4m-2-2h4m5-16l2.286 6.857L21 12l-5.714 2.143L13 21l-2.286-6.857L5 12l5.714-2.143L13 3z" />
                </svg>
                <span>Какво ново?</span>
                <span className="ml-auto text-xs text-gray-400">v{CURRENT_VERSION}</span>
              </button>

              <button
                onClick={() => { onShowChangelog(); setMenuOpen(false); }}
                className="w-full px-4 py-2 text-left text-sm text-gray-700 dark:text-dark-text-primary hover:bg-gray-100 dark:hover:bg-dark-bg-input flex items-center space-x-2"
              >
                <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 8v4l3 3m6-3a9 9 0 11-18 0 9 9 0 0118 0z" />
                </svg>
                <span>История на версиите</span>
              </button>
            </div>
          )}
        </div>
      </div>
    </div>
  );
};
